import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  AutocompleteElement,
  CheckboxButtonGroup,
  CheckboxElement,
  DatePickerElement,
  FormContainer,
  RadioButtonGroup,
  SwitchElement,
  TextFieldElement,
} from 'react-hook-form-mui'
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  Typography,
} from '@mui/material'
import * as dateFns from 'date-fns'
import { useAuth } from '../../hooks/context/UserAuthContext'
import {
  useGetTyreByLocalNumber,
  useGetTyreLocalNumbers,
  useGetTyreModels,
  useGetTyreSizes,
} from '../../hooks/api/tyres'
import { useCreateWarrantyRegistration } from '../../hooks/api/warranty'
import getSubmissionErrors from '../../utils/submissionErrors'
import { useNavigate } from 'react-router-dom'
import TyreSizeField from '../ui/form/TyreSizeField'
import { getBrandOptions } from '../../constants/brands'
import { getSimpleTyreSize, getTyreSize } from '../../utils/tyreSize'
import { useQueryClient } from 'react-query'
import CustomerForm from './CustomerForm'
import WarrantyRegistrationView from '../data/warranty/WarrantyRegistrationView'
import FileField from '../ui/form/FileField'
import { pickBy, identity } from 'lodash'
import { useSnackbar } from 'notistack'

const FILE_SIZE = 2000000
const SUPPORTED_FORMATS = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
}

const WarrantyRegistrationForm = ({ dealershipBrand }) => {
  const brandOptions = getBrandOptions(dealershipBrand)
  const { user } = useAuth()
  const [submitted, setSubmitted] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const queryClient = useQueryClient()
  const { mutateAsync } = useCreateWarrantyRegistration()

  const formContext = useForm({
    criteriaMode: 'all',
    defaultValues: {
      dateOfPurchase: new Date(),
      tyreSelectionMode: {
        front: 'manual',
        rear: 'manual',
      },
    },
  })
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    reset,
    resetField,
    getValues,
    setError,
    setValue,
  } = formContext

  const [dealershipBrandLocal, setDealershipBrandLocal] = useState(null)
  const dealershipBrandValue = user?.dealershipBrand || dealershipBrand || dealershipBrandLocal

  const tyreFrontBrand = watch('tyres.front.brand', null)
  const tyreRearBrand = watch('tyres.rear.brand', null)

  const enableFrontTyres = watch('enabledTyres.front', false)
  const enableRearTyres = watch('enabledTyres.rear', false)

  const rearSameAsFront = watch('rearSameAsFront', false)

  const tyreSelectionMode = {
    front: watch('tyreSelectionMode.front', 'manual'),
    rear: watch('tyreSelectionMode.rear', 'manual'),
  }

  const tyreFrontLocalNum = watch('tyres.front.localNum', null)
  const tyreRearLocalNum = watch('tyres.rear.localNum', null)

  const { data: tyreFrontModelOptions, isLoading: isLoadingTyreFrontModels } = useGetTyreModels(
    { brand: tyreFrontBrand?.name, dealershipBrand: dealershipBrandValue },
    { enabled: Boolean(tyreFrontBrand?.name) }
  )
  const { data: tyreFrontSizeOptions, isLoading: isLoadingTyreFrontSizes } = useGetTyreSizes(
    { brand: tyreFrontBrand?.name, dealershipBrand: dealershipBrandValue },
    { enabled: Boolean(tyreFrontBrand?.name) }
  )

  const { data: tyreRearModelOptions, isLoading: isLoadingTyreRearModels } = useGetTyreModels(
    { brand: tyreRearBrand?.name, dealershipBrand: dealershipBrandValue },
    { enabled: Boolean(tyreRearBrand?.name) }
  )
  const { data: tyreRearSizeOptions, isLoading: isLoadingTyreRearSizes } = useGetTyreSizes(
    { brand: tyreRearBrand?.name, dealershipBrand: dealershipBrandValue },
    { enabled: Boolean(tyreRearBrand?.name) }
  )

  const { data: tyreFrontLocalNums, isLoading: isLoadingTyreFrontLocalNums } =
    useGetTyreLocalNumbers(
      { dealershipBrand: dealershipBrandValue },
      { enabled: enableFrontTyres && tyreSelectionMode.front === 'localNums' }
    )
  const { data: tyreRearLocalNums, isLoading: isLoadingTyreRearLocalNums } = useGetTyreLocalNumbers(
    { dealershipBrand: dealershipBrandValue },
    { enabled: enableRearTyres && tyreSelectionMode.rear === 'localNums' }
  )

  const {
    data: tyreFrontLocal,
    error: tyreFrontLocalError,
    isLoading: isLoadingTyreFrontLocal,
  } = useGetTyreByLocalNumber(
    tyreFrontLocalNum,
    { dealershipBrand: dealershipBrandValue },
    {
      enabled: enableFrontTyres && tyreSelectionMode.front === 'localNums',
      retry: 1,
    }
  )

  const {
    data: tyreRearLocal,
    error: tyreRearLocalError,
    isLoading: isLoadingTyreRearLocal,
  } = useGetTyreByLocalNumber(
    tyreRearLocalNum,
    { dealershipBrand: dealershipBrandValue },
    {
      enabled: enableRearTyres && tyreSelectionMode.rear === 'localNums',
      retry: 1,
    }
  )

  useEffect(() => {
    if (tyreSelectionMode.front !== 'localNums') {
      resetField('tyres.front.model')
      resetField('tyres.front.size')
    }
    // eslint-disable-next-line
  }, [tyreFrontBrand])

  useEffect(() => {
    if (tyreSelectionMode.rear !== 'localNums') {
      resetField('tyres.rear.model')
      resetField('tyres.rear.size')
    }
    // eslint-disable-next-line
  }, [tyreRearBrand])

  useEffect(() => {
    if (!enableFrontTyres) {
      resetField('tyres.front.localNum')
      resetField('tyres.front.brand')
      resetField('rearSameAsFront')
    }
  }, [enableFrontTyres])

  useEffect(() => {
    if (!enableRearTyres) {
      resetField('tyres.rear.localNum')
      resetField('tyres.rear.brand')
    }
  }, [enableRearTyres])

  useEffect(() => {
    resetField('tyres.front.localNum')
    if (tyreSelectionMode.front === 'localNums') {
      resetField('tyres.front.brand')
      resetField('tyres.front.model')
      resetField('tyres.front.size')
    }
  }, [tyreSelectionMode.front])

  useEffect(() => {
    resetField('tyres.rear.localNum')
    if (tyreSelectionMode.rear === 'localNums') {
      resetField('tyres.rear.brand')
      resetField('tyres.rear.model')
      resetField('tyres.rear.size')
    }
  }, [tyreSelectionMode.rear])

  useEffect(() => {
    if (tyreSelectionMode.front === 'localNums') {
      resetField('tyres.front.brand')
      resetField('tyres.front.model')
      resetField('tyres.front.size')
    }
    // eslint-disable-next-line
  }, [tyreFrontLocalNum])

  useEffect(() => {
    if (tyreSelectionMode.rear === 'localNums') {
      resetField('tyres.rear.brand')
      resetField('tyres.rear.model')
      resetField('tyres.rear.size')
    }
    // eslint-disable-next-line
  }, [tyreRearLocalNum])

  useEffect(() => {
    if (enableFrontTyres && tyreFrontLocalNum && tyreSelectionMode.front === 'localNums') {
      setValue('tyres.front.brand', tyreFrontLocal?.brand ?? null)
      setValue('tyres.front.model', tyreFrontLocal?.model ?? null)
      setValue('tyres.front.size', tyreFrontLocal?.size ?? null)
    }
    // eslint-disable-next-line
  }, [tyreFrontLocal])

  useEffect(() => {
    if (enableRearTyres && tyreRearLocalNum && tyreSelectionMode.rear === 'localNums') {
      setValue('tyres.rear.brand', tyreRearLocal?.brand ?? null)
      setValue('tyres.rear.model', tyreRearLocal?.model ?? null)
      setValue('tyres.rear.size', tyreRearLocal?.size ?? null)
    }
    // eslint-disable-next-line
  }, [tyreRearLocal])

  useEffect(() => {
    resetField('tyres.front.localNum')
    resetField('tyres.rear.localNum')
    if (!dealershipBrandValue) {
      resetField('tyreSelectionMode.front')
      resetField('tyreSelectionMode.rear')
    } else {
      resetField('tyres.front.brand')
      resetField('tyres.rear.brand')
    }
  }, [dealershipBrandValue])

  const onSubmit = async (data) => {
    try {
      if (data.tyres) {
        if (!enableFrontTyres && data.tyres.front) {
          delete data.tyres.front
        }
        if (!enableRearTyres && data.tyres.rear) {
          delete data.tyres.rear
        }
        if (rearSameAsFront && enableFrontTyres && enableRearTyres) {
          data.tyres.rear = { ...data.tyres.front }
        }
      }

      let values = pickBy(
        {
          ...data,
          dealership: data.dealership?._id || data.dealership?.id || data.dealership,
          enabledTyres: {
            front: enableFrontTyres,
            rear: enableRearTyres,
          },
        },
        identity
      )

      delete values['enabledTyres.front']
      delete values['enabledTyres.rear']

      // Convert to FormData
      let formData = new FormData()
      for (let key in values) {
        if (values.hasOwnProperty(key)) {
          if (['salesInvoice'].includes(key)) {
            formData.append(key, values[key][0])
          } else if (['warrantyCertificate'].includes(key)) {
            for (let i = 0; i < values[key].length; i++) {
              formData.append(key, values[key][i])
            }
          } else if (values[key] instanceof Date) {
            formData.append(key, dateFns.format(values[key], 'yyyy-MM-dd'))
          } else if (typeof values[key] === 'string' || values[key] instanceof Date) {
            formData.append(key, values[key])
          } else {
            formData.append(key, JSON.stringify(values[key]))
          }
        }
      }
      const res = await mutateAsync(formData)
      queryClient.invalidateQueries(['warranty'])
      setSubmitted(res?.data)
    } catch (err) {
      const errors = getSubmissionErrors(err) || []
      for (const { name, message } of errors) {
        setError(name, { message })
      }
      enqueueSnackbar(
        errors?.length > 0
          ? 'Please check the form before submission.'
          : err?.message || 'A submission error occurred.',
        { variant: 'error' }
      )
    }
  }

  const isLoading =
    isLoadingTyreFrontLocal ||
    isLoadingTyreFrontLocalNums ||
    isLoadingTyreFrontModels ||
    isLoadingTyreFrontSizes ||
    isLoadingTyreRearLocal ||
    isLoadingTyreRearLocalNums ||
    isLoadingTyreRearModels ||
    isLoadingTyreRearSizes

  if (submitted) {
    return (
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        <Typography variant='h5'>Thank you</Typography>
        <Typography variant='h6'>
          You have successfully submitted your warranty registration
        </Typography>
        <WarrantyRegistrationView data={submitted} showDealershipName alwaysExpand />
      </Box>
    )
  }
  return (
    <FormContainer formContext={formContext} handleSubmit={handleSubmit(onSubmit)}>
      <Box display='flex' flexDirection='column' rowGap={2} justifyContent='space-between'>
        <Typography variant='h6'>Customer Details</Typography>
        <Grid container spacing={2}>
          <CustomerForm
            disableEdit
            dealershipBrand={dealershipBrand}
            onDealershipChange={(event, dealership) => setDealershipBrandLocal(dealership?.brand)}
          />
        </Grid>
        <Divider />
        <Typography variant='h6'>Product Details</Typography>
        <DatePickerElement
          name='dateOfPurchase'
          label='Tyre Purchase Date'
          disableFuture
          openTo='day'
          views={['year', 'month', 'day']}
          minDate={dateFns.sub(new Date(), { days: 14 })}
          inputFormat='dd/MM/yyyy'
          required
          validation={{
            required: 'Tyre purchase date is required.',
            valueAsDate: 'Invalid tyre purchase date.',
          }}
        />
        <TextFieldElement
          name='mileageOnPurchase'
          label='Mileage on Purchase'
          type='number'
          InputProps={{ endAdornment: <InputAdornment position='end'>km</InputAdornment> }}
          required
          validation={{
            required: 'Mileage on purchase is required.',
            //valueAsNumber: 'Mileage on purchase must be a valid number.',
            min: { value: 0, message: 'Mileage on purchase must be a valid number.' },
          }}
        />
        <TextFieldElement
          name='chassisNumber'
          label='Chassis No.'
          inputProps={{
            style: { textTransform: 'uppercase' },
          }}
        />

        <TextFieldElement
          name='vehicleNumber'
          label='Vehicle Registration Number'
          inputProps={{
            style: { textTransform: 'uppercase' },
          }}
          required
          validation={{
            required: 'Vehicle registration number is required.',
          }}
        />

        <TextFieldElement
          name='salesInvoiceNumber'
          label='Sales Invoice Number'
          inputProps={{
            style: { textTransform: 'uppercase' },
          }}
          required
          validation={{
            required: 'Sales invoice number is required.',
          }}
        />

        <FileField
          inputProps={{
            ...register('salesInvoice', {
              validate: (value) => {
                if (!(value?.length > 0)) {
                  return 'Sales invoice is required.'
                }
                for (const file of value || []) {
                  if (file.size > FILE_SIZE) {
                    return 'Sales invoice file must be smaller than 2 MB.'
                  }
                }
                return true
              },
            }),
            onChange: (e) => {
              setValue('salesInvoice', e?.target?.files)
            },
            type: 'file',
            accept: SUPPORTED_FORMATS,
          }}
          label='Sales Invoice'
          required
          error={Boolean(errors?.['salesInvoice'])}
          helperText={errors?.['salesInvoice']?.message}
        />
        {['bmw'].includes(dealershipBrandValue) && (
          <FileField
            inputProps={{
              ...register('warrantyCertificate', {
                validate: {
                  fileSize: (value) => {
                    for (const file of value || []) {
                      if (file.size > FILE_SIZE) {
                        return 'Warranty certificate file must be smaller than 2 MB.'
                      }
                    }
                    return true
                  },
                },
              }),
              onChange: (e) => {
                setValue('warrantyCertificate', e?.target?.files)
              },
              type: 'file',
              accept: SUPPORTED_FORMATS,
              multiple: true,
            }}
            label='Warranty Certificates'
            error={Boolean(errors?.['warrantyCertificate'])}
            helperText={errors?.['warrantyCertificate']?.message}
          />
        )}
        <Divider />
        <Typography variant='h6'>Tyres</Typography>
        {[
          {
            title: 'Front Tyres',
            name: 'front',
            enabled: enableFrontTyres,
            tyreModelOptions: tyreFrontModelOptions,
            tyreSizeOptions: tyreFrontSizeOptions,
            tyreLocalNumsOptions: tyreFrontLocalNums,
            tyreLocal: tyreFrontLocal,
            isLoadingTyreModels: isLoadingTyreFrontModels,
            isLoadingTyreSizes: isLoadingTyreFrontSizes,
            isLoadingTyreLocalNums: isLoadingTyreFrontLocalNums,
            isLoadingTyreLocal: isLoadingTyreFrontLocal,
            tyreLocalError: tyreFrontLocalError,
          },
          {
            title: 'Rear Tyres',
            name: 'rear',
            enabled: enableRearTyres,
            tyreModelOptions: tyreRearModelOptions,
            tyreSizeOptions: tyreRearSizeOptions,
            tyreLocalNumsOptions: tyreRearLocalNums,
            tyreLocal: tyreRearLocal,
            isLoadingTyreModels: isLoadingTyreRearModels,
            isLoadingTyreSizes: isLoadingTyreRearSizes,
            isLoadingTyreLocalNums: isLoadingTyreRearLocalNums,
            isLoadingTyreLocal: isLoadingTyreRearLocal,
            tyreLocalError: tyreRearLocalError,
          },
        ].map(
          ({
            title,
            name,
            enabled,
            tyreModelOptions,
            tyreSizeOptions,
            tyreLocalNumsOptions,
            tyreLocal,
            isLoadingTyreModels,
            isLoadingTyreSizes,
            isLoadingTyreLocalNums,
            isLoadingTyreLocal,
            tyreLocalError,
          }) => (
            <React.Fragment key={name}>
              <CheckboxElement name={`enabledTyres.${name}`} label={title} />
              <Collapse in={enabled} unmountOnExit>
                <Box
                  display='flex'
                  flexDirection='column'
                  rowGap={2}
                  justifyContent='space-between'
                >
                  <Typography variant='h6'>{title}</Typography>
                  {enableFrontTyres && name === 'rear' && (
                    <SwitchElement name='rearSameAsFront' label='Same as front tyres' />
                  )}
                  {!(rearSameAsFront && enableFrontTyres && name === 'rear') && (
                    <>
                      {dealershipBrandValue && (
                        <RadioButtonGroup
                          name={`tyreSelectionMode.${name}`}
                          label='Tyre Selection Mode'
                          options={[
                            {
                              id: 'manual',
                              label: 'Select tyre manually',
                            },
                            {
                              id: 'localNums',
                              label: 'Select tyre by part number',
                            },
                          ]}
                          defaultValue='manual'
                          required
                          validation={{
                            required: 'Please select a method of tyre selection.',
                          }}
                        />
                      )}
                      {tyreSelectionMode[name] === 'localNums' && (
                        <>
                          <AutocompleteElement
                            name={`tyres.${name}.localNum`}
                            label='Part Number'
                            options={tyreLocalNumsOptions || []}
                            loading={isLoadingTyreLocalNums}
                            required
                            autocompleteProps={{
                              isOptionEqualToValue: (option, value) => option === value,
                            }}
                            rules={{
                              required: 'Part number is required.',
                            }}
                          />
                          <FormHelperText error={Boolean(tyreLocalError)}>
                            {tyreLocalError?.response?.data?.message ||
                              (isLoadingTyreLocal || isLoadingTyreModels || isLoadingTyreSizes
                                ? 'Please wait...'
                                : 'Select a part number.')}
                          </FormHelperText>
                        </>
                      )}

                      <AutocompleteElement
                        name={`tyres.${name}.brand`}
                        label='Tyre Brand'
                        options={brandOptions || []}
                        loading={tyreSelectionMode[name] === 'localNums' && isLoadingTyreLocal}
                        autocompleteProps={{
                          disabled: tyreSelectionMode[name] === 'localNums',
                          isOptionEqualToValue: (option, value) => {
                            return option.id === (value.id ?? value.name)
                          },
                          getOptionLabel: (option) => {
                            return option.fullName || option.name || option
                          },
                        }}
                        required
                        rules={{
                          required: 'Tyre brand is required.',
                        }}
                      />
                      <AutocompleteElement
                        name={`tyres.${name}.model`}
                        label='Tyre Model'
                        options={tyreModelOptions || []}
                        loading={
                          (tyreSelectionMode[name] === 'localNums' && isLoadingTyreLocal) ||
                          isLoadingTyreModels
                        }
                        autocompleteProps={{
                          clearOnBlur: true,
                          freeSolo: true,
                          disabled: tyreSelectionMode[name] === 'localNums',
                          isOptionEqualToValue: (option, value) => {
                            return option?.id == (value?.id ?? value?.name)
                          },
                          getOptionLabel: (option) => {
                            return option?.fullName || option?.name || option
                          },
                        }}
                        required
                        rules={{
                          required: 'Tyre model is required.',
                          validate: (model) => model?.id === model?.name || 'Invalid tyre model.',
                        }}
                      />
                      <TyreSizeField
                        control={control}
                        textFieldProps={{ required: true }}
                        rules={{
                          required: 'Tyre size is required.',
                          validate: (size) =>
                            size?.id === getSimpleTyreSize(size) || 'Invalid tyre size.',
                        }}
                        clearOnBlur
                        freeSolo
                        name={`tyres.${name}.size`}
                        label='Tyre Size'
                        options={tyreSizeOptions || []}
                        loading={
                          (tyreSelectionMode[name] === 'localNums' && isLoadingTyreLocal) ||
                          isLoadingTyreSizes
                        }
                        disabled={tyreSelectionMode[name] === 'localNums'}
                      />
                    </>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          )
        )}

        <Divider />
        <CheckboxElement
          name='pdpaAgreement'
          label={
            <>
              By clicking submit you agree to our{' '}
              <Link component='a' href='/privacy-policy' target='_blank' rel='noopener noreferrer'>
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link
                component='a'
                href='/terms-and-conditions'
                target='_blank'
                rel='noopener noreferrer'
              >
                Terms and Conditions
              </Link>
              .
            </>
          }
          required
          validation={{
            required: 'You must agree to our Privacy Policy and Terms of Conditions.',
          }}
        />

        <Box gap={2} display='flex' justifyContent='flex-end'>
          <Button
            type='button'
            onClick={() => {
              reset()
            }}
            disabled={isSubmitting}
          >
            Reset
          </Button>
          <Button type='submit' variant='contained' disabled={isSubmitting || isLoading}>
            Submit
          </Button>
        </Box>
      </Box>
    </FormContainer>
  )
}

export default WarrantyRegistrationForm
